import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";

const NAV = [
  { label: "首页", to: "/" },
  { label: "电影分类", to: "/category" },
  { label: "搜索", to: "/search" },
  { label: "关于我们", to: "/about" },
];

export default function SiteHeader() {
  const location = useLocation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [keyword, setKeyword] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    setKeyword(params.get("q") ?? "");
    setMenuOpen(false);
  }, [location.pathname, params]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    navigate(q ? `/search?q=${encodeURIComponent(q)}` : "/search");
    setMenuOpen(false);
  };

  const isActive = (to: string) =>
    to === "/" ? location.pathname === "/" : location.pathname.startsWith(to);

  return (
    <header
      className={`sticky top-0 z-50 w-full border-b transition-colors duration-300 ${
        scrolled
          ? "bg-background-50/95 backdrop-blur border-background-200"
          : "bg-background-50 border-transparent"
      }`}
    >
      <div className="w-full px-4 md:px-8">
        <div className="flex items-center justify-between h-16 md:h-20 gap-4">
          {/* Brand */}
          <Link to="/" className="flex items-center gap-2.5 shrink-0 cursor-pointer">
            <span className="w-9 h-9 rounded-lg bg-primary-500 flex items-center justify-center text-background-50">
              <i className="ri-movie-2-line text-xl"></i>
            </span>
            <span className="flex flex-col leading-none">
              <span className="font-heading font-bold text-lg text-foreground-950 whitespace-nowrap">
                蜜桃成熟时
              </span>
              <span className="text-[11px] tracking-[0.28em] text-primary-600 font-label pl-0.5">
                MOVIE
              </span>
            </span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-1">
            {NAV.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className={`px-4 py-2 rounded-md text-sm font-label transition-colors cursor-pointer whitespace-nowrap ${
                  isActive(item.to)
                    ? "text-primary-700 bg-primary-100/70 font-medium"
                    : "text-foreground-700 hover:text-primary-600 hover:bg-background-100"
                }`}
              >
                {item.label}
              </Link>
            ))}
          </nav>

          {/* Desktop search */}
          <form onSubmit={submitSearch} className="hidden lg:flex items-center gap-2 w-72">
            <div className="relative flex-1">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-secondary-400 text-base"></i>
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索电影 / 演员"
                className="w-full h-10 pl-9 pr-3 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200/60 transition"
              />
            </div>
            <button
              type="submit"
              className="h-10 px-4 rounded-md bg-primary-500 text-background-50 text-sm font-label font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              搜索
            </button>
          </form>

          {/* Mobile actions */}
          <div className="flex items-center gap-1 md:hidden">
            <Link
              to="/search"
              className="w-10 h-10 flex items-center justify-center rounded-md text-foreground-700 hover:bg-background-100 cursor-pointer"
              aria-label="搜索"
            >
              <i className="ri-search-line text-xl"></i>
            </Link>
            <button
              type="button"
              onClick={() => setMenuOpen((v) => !v)}
              className="w-10 h-10 flex items-center justify-center rounded-md text-foreground-800 hover:bg-background-100 cursor-pointer"
              aria-label="菜单"
            >
              <i className={`text-2xl ${menuOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile panel */}
      {menuOpen && (
        <div className="md:hidden border-t border-background-200 bg-background-50 px-4 py-4 animate-fade-in">
          <form onSubmit={submitSearch} className="flex items-center gap-2 mb-3">
            <div className="relative flex-1">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-secondary-400 text-base"></i>
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索电影 / 演员"
                className="w-full h-11 pl-9 pr-3 rounded-md bg-background-100 border border-background-200 text-sm focus:outline-none focus:border-primary-400"
              />
            </div>
            <button
              type="submit"
              className="h-11 px-4 rounded-md bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap cursor-pointer"
            >
              搜索
            </button>
          </form>
          <nav className="flex flex-col">
            {NAV.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className={`px-3 py-3 rounded-md text-sm font-label cursor-pointer ${
                  isActive(item.to)
                    ? "text-primary-700 bg-primary-100/70 font-medium"
                    : "text-foreground-700"
                }`}
              >
                {item.label}
              </Link>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}